import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { formatETH } from '../../lib/utils';
import { format } from 'date-fns';
import { Loader2, AlertTriangle, ExternalLink, XCircle, Trophy, Timer } from 'lucide-react';

function TxLink({ hash, labelKey }) {
    const { t } = useTranslation();
    return (
        <div className="p-3 rounded-lg bg-[#00FF66]/10 border border-[#00FF66]/20 mb-4">
            <div className="text-xs text-[#00FF66] mb-1">{t(labelKey)}</div>
            <a
                href={`https://basescan.org/tx/${hash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-xs text-white hover:text-[#00FF66] flex items-center gap-1"
            >
                {hash.slice(0, 10)}...{hash.slice(-8)}
                <ExternalLink className="w-3 h-3" />
            </a>
        </div>
    );
}

export function BetActionsPanel({
    bet,
    canTake,
    canCancel,
    canResolve,
    isCorrectChain,
    hasEnoughBalance,
    totalPool,
    actionLoading,
    acceptHash,
    cancelHash,
    isAcceptPending,
    isAcceptConfirming,
    isCancelPending,
    isCancelConfirming,
    onTake,
    onCancel,
    onResolve,
    dateLocale,
}) {
    const { t } = useTranslation();
    return (
        <Card className="bg-white/[0.02] border-white/5 sticky top-24">
            <CardContent className="p-6 space-y-4">
                {/* Take Bet */}
                {canTake && (
                    <>
                        <div className="p-4 rounded-xl bg-[#00FF66]/10 border border-[#00FF66]/20 mb-4">
                            <div className="text-sm text-white/60 mb-2">{t('betDetail.toTakeStake')}</div>
                            <div className="font-mono text-3xl text-[#00FF66] font-bold">{formatETH(bet.taker_stake)} ETH</div>
                            <div className="text-xs text-white/40 mt-2">{t('betDetail.ifYouWin', { eth: formatETH(totalPool * 0.99) })}</div>
                        </div>

                        {!isCorrectChain && (
                            <Alert className="border-[#FFD700]/30 bg-[#FFD700]/10 mb-4">
                                <AlertTriangle className="w-4 h-4 text-[#FFD700]" />
                                <AlertDescription className="text-white/60">{t('betDetail.switchToBaseHint')}</AlertDescription>
                            </Alert>
                        )}

                        {!hasEnoughBalance && isCorrectChain && (
                            <Alert className="border-[#FFD700]/30 bg-[#FFD700]/10 mb-4">
                                <AlertTriangle className="w-4 h-4 text-[#FFD700]" />
                                <AlertDescription className="text-white/60">
                                    {t('betDetail.insufficientBalance', { eth: formatETH(bet.taker_stake) })}
                                </AlertDescription>
                            </Alert>
                        )}

                        {acceptHash && <TxLink hash={acceptHash} labelKey="betDetail.transaction" />}

                        <Button
                            onClick={onTake}
                            disabled={actionLoading || !hasEnoughBalance || !isCorrectChain || isAcceptPending || isAcceptConfirming}
                            className="w-full bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] hover:shadow-[0_0_20px_rgba(130,255,122,0.4)] font-semibold py-6 text-lg"
                            data-testid="take-bet-button"
                        >
                            {isAcceptPending ? (
                                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t('betDetail.confirmInWallet')}</>
                            ) : isAcceptConfirming ? (
                                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t('betDetail.confirming')}</>
                            ) : actionLoading === 'take' ? (
                                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t('betDetail.takingBet')}</>
                            ) : (
                                <>{t('betDetail.takeThisBet', { eth: formatETH(bet.taker_stake) })}</>
                            )}
                        </Button>
                    </>
                )}

                {/* Cancel */}
                {canCancel && (
                    <>
                        {cancelHash && <TxLink hash={cancelHash} labelKey="betDetail.cancelTransaction" />}
                        <Button
                            onClick={onCancel}
                            disabled={actionLoading || isCancelPending || isCancelConfirming}
                            variant="outline"
                            className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                            data-testid="cancel-bet-button"
                        >
                            {isCancelPending ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('betDetail.confirmInWallet')}</>
                            ) : isCancelConfirming || actionLoading === 'cancel' ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('betDetail.cancelling')}</>
                            ) : (
                                <><XCircle className="w-4 h-4 mr-2" />{t('betDetail.cancelBet')}</>
                            )}
                        </Button>
                    </>
                )}

                {/* Resolve */}
                {canResolve && (
                    <div className="space-y-3">
                        <div className="text-sm text-white/50 text-center mb-2">{t('betDetail.resolutionPassed')}</div>
                        <Button
                            onClick={() => onResolve('creator_wins')}
                            disabled={actionLoading}
                            className="w-full bg-[#00FF66]/20 text-[#00FF66] hover:bg-[#00FF66]/30 border border-[#00FF66]/30"
                            data-testid="resolve-creator-wins"
                        >
                            {actionLoading === 'creator_wins' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trophy className="w-4 h-4 mr-2" />}
                            {t('betDetail.creatorWins')}
                        </Button>
                        <Button
                            onClick={() => onResolve('taker_wins')}
                            disabled={actionLoading}
                            className="w-full bg-[#6F3FCA]/20 text-[#9B6FE8] hover:bg-[#6F3FCA]/30 border border-[#6F3FCA]/30"
                            data-testid="resolve-taker-wins"
                        >
                            {actionLoading === 'taker_wins' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trophy className="w-4 h-4 mr-2" />}
                            {t('betDetail.takerWins')}
                        </Button>
                    </div>
                )}

                {/* Status info */}
                {bet.status === 'active' && !canResolve && (
                    <div className="text-center p-4 rounded-lg bg-[#6F3FCA]/10 border border-[#6F3FCA]/20">
                        <Timer className="w-8 h-8 text-[#9B6FE8] mx-auto mb-2" />
                        <div className="text-[#9B6FE8] font-semibold mb-1">{t('betDetail.betActive')}</div>
                        <div className="text-sm text-white/50">
                            {t('betDetail.waitingResolution', { date: format(new Date(bet.resolution_time), 'PPP', { locale: dateLocale }) })}
                        </div>
                    </div>
                )}

                {bet.status === 'resolved' && (
                    <div className="text-center p-4 rounded-lg bg-[#6F3FCA]/10 border border-[#6F3FCA]/20">
                        <Trophy className="w-8 h-8 text-[#9B6FE8] mx-auto mb-2" />
                        <div className="text-[#9B6FE8] font-semibold">{t('betDetail.betResolved')}</div>
                    </div>
                )}

                {bet.status === 'cancelled' && (
                    <div className="text-center p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                        <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                        <div className="text-red-400 font-semibold">{t('betDetail.betCancelled')}</div>
                    </div>
                )}

                {/* Timestamps */}
                <div className="pt-4 border-t border-white/10 space-y-2 text-xs text-white/40">
                    <div className="flex justify-between">
                        <span>{t('betDetail.created')}</span>
                        <span>{format(new Date(bet.created_at), 'MMM d, yyyy HH:mm')}</span>
                    </div>
                    {bet.accepted_at && (
                        <div className="flex justify-between">
                            <span>{t('betDetail.accepted')}</span>
                            <span>{format(new Date(bet.accepted_at), 'MMM d, yyyy HH:mm')}</span>
                        </div>
                    )}
                    <div className="flex justify-between">
                        <span>{t('betDetail.resolution')}</span>
                        <span>{format(new Date(bet.resolution_time), 'MMM d, yyyy HH:mm')}</span>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
